/* Hikari Vault - styles.js
   Centralised design tokens + inline-style factories.
   Aesthetic: dark monochrome with cyan accent. DM Mono typeface.
*/

export const C = {
    bg: "#07090D",
    surface: "rgba(255,255,255,0.03)",
    surfaceHover: "rgba(255,255,255,0.055)",
    border: "rgba(255,255,255,0.08)",
    borderStrong: "rgba(255,255,255,0.14)",
    text: "#E2E8F0",
    muted: "#6B7A8D",
    dim: "#4A5568",
    accent: "#0B7EA3",
    accentGlow: "rgba(11,126,163,0.18)",
    danger: "#D92B2B",
    warn: "#D97706",
    warnSoft: "#EAB308",
    ok: "#059669",
    font: "'DM Mono', ui-monospace, 'SF Mono', Menlo, monospace",
};

export const CRED_TYPES = [
    {
        id: "api",
        label: "API Key",
        icon: "zap",
        color: "#0B7EA3",
        fields: ["Service", "Key", "Environment", "Expiry"],
    },
    {
        id: "password",
        label: "Password",
        icon: "key-round",
        color: "#7C3AED",
        fields: ["Service", "Username", "Password", "URL", "Expiry"],
    },
    {
        id: "database",
        label: "Database",
        icon: "database",
        color: "#059669",
        fields: ["Service", "Connection URL", "Username", "Password", "Expiry"],
    },
    {
        id: "legal",
        label: "Legal",
        icon: "file-badge",
        color: "#D97706",
        fields: ["Document", "Value", "Issued By", "Expiry"],
    },
    {
        id: "note",
        label: "Note",
        icon: "file-text",
        color: "#6B7280",
        fields: ["Title", "Content"],
    },
];

export const fmtDate = (iso) =>
    iso
        ? new Date(iso).toLocaleDateString("en-CA", {
              month: "short",
              day: "numeric",
              year: "numeric",
          })
        : "—";

export const daysLeft = (iso) => {
    if (!iso) return null;
    return Math.ceil((new Date(iso) - new Date()) / 86400000);
};

export const expColor = (iso) => {
    const d = daysLeft(iso);
    if (d === null) return null;
    if (d < 0) return C.danger;
    if (d <= 7) return C.warn;
    if (d <= 30) return C.warnSoft;
    return C.ok;
};

export const expLabel = (iso) => {
    const d = daysLeft(iso);
    if (d === null) return null;
    if (d < 0) return `Expired ${Math.abs(d)}d ago`;
    if (d === 0) return "Expires today";
    if (d <= 30) return `${d}d left`;
    return fmtDate(iso);
};

// ── Inline-style factories ──
export const sBtn = (variant = "primary", full = true) => ({
    width: full ? "100%" : "auto",
    padding: full ? "14px 20px" : "10px 18px",
    background:
        variant === "primary"
            ? `linear-gradient(135deg, ${C.accent}, #065670)`
            : variant === "danger"
              ? "rgba(217,43,43,0.12)"
              : variant === "ghost"
                ? "transparent"
                : "rgba(255,255,255,0.04)",
    border:
        variant === "ghost"
            ? `1px solid ${C.border}`
            : variant === "danger"
              ? "1px solid rgba(217,43,43,0.25)"
              : variant === "secondary"
                ? `1px solid ${C.border}`
                : "none",
    borderRadius: 10,
    color:
        variant === "danger"
            ? C.danger
            : variant === "ghost"
              ? C.muted
              : variant === "secondary"
                ? C.text
                : "#fff",
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: "0.04em",
    cursor: "pointer",
    marginBottom: 10,
    fontFamily: C.font,
    transition: "opacity 0.15s, background 0.15s, transform 0.08s",
});

export const sInp = (big = false) => ({
    width: "100%",
    padding: big ? "14px 16px" : "11px 14px",
    background: "rgba(255,255,255,0.04)",
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    color: C.text,
    fontSize: big ? 18 : 13,
    outline: "none",
    marginBottom: 10,
    boxSizing: "border-box",
    fontFamily: C.font,
    letterSpacing: big ? "0.3em" : "normal",
    textAlign: big ? "center" : "left",
});

export const sBadge = (color) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 10px",
    borderRadius: 100,
    background: `${color}18`,
    border: `1px solid ${color}30`,
    color,
    fontSize: 11,
    letterSpacing: "0.06em",
    fontFamily: C.font,
});

export const sPill = (active, color = C.accent) => ({
    padding: "7px 14px",
    borderRadius: 100,
    fontSize: 12,
    cursor: "pointer",
    background: active ? `${color}22` : "transparent",
    border: `1px solid ${active ? color : C.border}`,
    color: active ? color : C.muted,
    fontFamily: C.font,
    marginRight: 6,
    marginBottom: 6,
    transition: "all 0.15s",
    userSelect: "none",
});

export const sCard = {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    padding: "16px 18px",
    marginBottom: 10,
    cursor: "pointer",
    transition: "border-color 0.2s, background 0.2s, transform 0.08s",
};

export const sTopBar = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 20px",
    borderBottom: `1px solid ${C.border}`,
    background: "rgba(7,9,13,0.92)",
    backdropFilter: "blur(20px)",
    position: "sticky",
    top: 0,
    zIndex: 10,
};

export const sFull = {
    position: "fixed",
    inset: 0,
    zIndex: 20,
    background: C.bg,
    display: "flex",
    flexDirection: "column",
    color: C.text,
    fontFamily: C.font,
};

export const sToast = {
    position: "fixed",
    bottom: 32,
    left: "50%",
    transform: "translateX(-50%)",
    background: "rgba(11,126,163,0.92)",
    color: "#fff",
    padding: "11px 28px",
    borderRadius: 100,
    fontSize: 13,
    zIndex: 100,
    boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
    backdropFilter: "blur(12px)",
    whiteSpace: "nowrap",
    fontFamily: C.font,
};

export const sGrid = {
    position: "fixed",
    inset: 0,
    zIndex: 0,
    pointerEvents: "none",
    backgroundImage:
        "linear-gradient(rgba(255,255,255,0.018) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.018) 1px, transparent 1px)",
    backgroundSize: "48px 48px",
};

export const sGlow = {
    position: "fixed",
    top: -300,
    left: "50%",
    transform: "translateX(-50%)",
    width: 800,
    height: 500,
    zIndex: 0,
    pointerEvents: "none",
    background:
        "radial-gradient(ellipse, rgba(11,126,163,0.08) 0%, transparent 65%)",
};

export const sShell = {
    minHeight: "100vh",
    background: C.bg,
    color: C.text,
    fontFamily: C.font,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    position: "relative",
    overflow: "hidden",
};

export const sPanel = {
    position: "relative",
    zIndex: 1,
    background: "rgba(255,255,255,0.025)",
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    padding: "44px 36px",
    width: "100%",
    maxWidth: 440,
    backdropFilter: "blur(20px)",
    boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
};
